// @givmo/app-charity — what a 409 on a Charity Terms acceptance MEANS.
//
// Two surfaces record an acceptance of this instrument and both can be refused by the same service
// (`charity_terms_service._resolve_document_for_acceptance`): the Owner's served accept
// (`POST …/terms/acceptance`, `terms-page.tsx`) and the claimant's bundled claim submit
// (`POST …/claim`, the CPA-WEB12 wizard). The refusals are properties of the request, so both surfaces
// must read them identically — and they must not be read by status alone, because the backend types no
// machine `error_code` on any of them: classification is by detail string, with broader patterns kept
// as drift tolerance behind the exact phrasing.
//
// ⚠ ORDER MATTERS AND IT IS THE SERVER'S ORDER. The EIN guard fires BEFORE the version/hash pin, so a
// no-EIN organization is refused for the EIN even when its echo is perfectly current. A caller that
// tested for staleness first would tell such a claimant to re-read a document that was never the
// problem, and the re-read would fail identically, forever. {@link classifyTermsAcceptanceConflict}
// encodes that order once.

import { firstErrorCode, isGivmoError } from '@givmo/api-client';

/**
 * Why the server refused to record this acceptance.
 *
 *   - `missing-ein`      — the organization has no EIN on file, so the §318(a) record would be legally
 *                          incomplete. NOT retryable by the person: no re-read changes it.
 *   - `stale-terms`      — the version/hash echoed is no longer current. Re-fetch and re-present; the
 *                          affirmation must be made again against the newly shown body.
 *   - `already-accepted` — the server reports an acceptance already exists.
 *   - `other-conflict`   — a 409 none of the patterns matched. Treated as staleness by callers, because
 *                          re-presenting the current document is never the wrong thing to do.
 *   - `null`             — not a 409 at all.
 */
export type TermsAcceptanceConflict =
  | 'missing-ein'
  | 'stale-terms'
  | 'already-accepted'
  | 'other-conflict'
  | null;

export function classifyTermsAcceptanceConflict(error: unknown): TermsAcceptanceConflict {
  if (!isGivmoError(error) || error.status !== 409) return null;
  if (isMissingEinConflict(error)) return 'missing-ein';
  if (isAlreadyAcceptedConflict(error)) return 'already-accepted';
  if (isStaleTermsConflict(error)) return 'stale-terms';
  return 'other-conflict';
}

function isMissingEinConflict(error: unknown): boolean {
  if (!isGivmoError(error)) return false;
  const code = firstErrorCode(error) ?? '';
  // The backend's CharityTermsEinRequiredException detail reads "Your organization's EIN is not on
  // file, ..." with no machine error_code — pin that phrasing first, keep broader patterns as drift
  // tolerance.
  return (
    /EIN is not on file/i.test(error.message) ||
    /(?:MISSING|REQUIRED|INVALID).*EIN|EIN.*(?:MISSING|REQUIRED|INVALID)/i.test(code) ||
    /\bEIN\b.*\b(?:missing|required|needed|profile|non-null)\b|\b(?:missing|required|needed|non-null)\b.*\bEIN\b/i.test(
      error.message,
    )
  );
}

function isAlreadyAcceptedConflict(error: unknown): boolean {
  if (!isGivmoError(error)) return false;
  const signal = `${firstErrorCode(error) ?? ''} ${error.message}`;
  return /already[_\s-]*accepted|acceptance[_\s-]*exists/i.test(signal);
}

function isStaleTermsConflict(error: unknown): boolean {
  if (!isGivmoError(error)) return false;
  const signal = `${firstErrorCode(error) ?? ''} ${error.message}`;
  // The backend's CharityTermsVersionMismatchException detail reads "The terms you viewed are no
  // longer current. ..." — pin that phrasing first. The bundled submit raises the SAME exception with
  // a different detail when any of the four terms/signer fields is absent ("Please review and accept
  // the current Charity Terms to claim this organization"), which `review and accept` catches: that
  // case only arises when the backend switch is on and the wizard build predates it, and
  // re-presenting the document is the one response that is never wrong.
  return /no longer current|review and accept|stale|mismatch|rotat|updated|changed|current.*(?:version|hash)|(?:version|hash).*current/i.test(
    signal,
  );
}
